/* Enterprise CRM marketing layer — catalog-driven, shared everywhere.
   Tiers come from GET /api/public/crm-catalog (single source of truth), so a
   CRM promoted to live on the backend updates every surface automatically. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, CheckCircle2, Plug2, Lock, Clock3, Sparkles, RefreshCw, Activity, PauseCircle, PlayCircle, Loader2, Info, AlertTriangle } from "lucide-react";

let CATALOG_CACHE = null;
export const useCrmCatalog = () => {
  const [cat, setCat] = useState(CATALOG_CACHE);
  useEffect(() => {
    if (CATALOG_CACHE) return;
    api.get("/public/crm-catalog").then(({ data }) => { CATALOG_CACHE = data; setCat(data); }).catch(() => {});
  }, []);
  return cat;
};

const TILE_COLORS = { clio: "bg-[#1657e2]", practicepanther: "bg-[#6d28d9]", mycase: "bg-[#0e9f6e]", needles: "bg-[#b45309]" };
const CrmTile = ({ k, name, dark, size = "md" }) => (
  <span className={`${size === "sm" ? "h-7 w-7 text-xs" : "h-10 w-10 text-sm"} rounded-xl ${TILE_COLORS[k] || "bg-slate-500"} text-white font-heading font-extrabold flex items-center justify-center shrink-0`}
    aria-label={name}>{name[0]}</span>
);

export const EnterpriseCapabilityPanel = ({ dark = false }) => {
  const cat = useCrmCatalog();
  if (!cat) return null;
  return (
    <div className={`rounded-3xl border p-6 ${dark ? "bg-white/5 border-white/10" : "bg-white border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"}`} data-testid="enterprise-capability-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck className={`w-5 h-5 ${dark ? "text-cyan-300" : "text-cobalt"}`} />
        <h3 className={`font-heading font-bold ${dark ? "text-white" : "text-navy"}`}>Enterprise Integration Capabilities</h3>
      </div>
      <p className={`mt-1 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>Every connected CRM inherits the full enterprise engine — not just a data export.</p>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {cat.enterprise_capabilities.map((c) => (
          <div key={c} className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs ${dark ? "bg-white/5 text-slate-300" : "bg-slate-50 text-slate-600"}`} data-testid={`capability-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-emerald-400" : "text-emerald-500"}`} /> {c}
          </div>
        ))}
      </div>
    </div>
  );
};

export const EnterpriseCrmSection = ({ dark = false, compact = false, showCapabilities = true }) => {
  const cat = useCrmCatalog();
  if (!cat) return null;
  const t = {
    heading: dark ? "text-white" : "text-navy",
    sub: dark ? "text-slate-300" : "text-slate-600",
    card: dark ? "bg-white/5 border-white/10" : "bg-white border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]",
    chip: dark ? "bg-white/10 text-slate-300" : "bg-slate-100 text-slate-500",
    eyebrow: dark ? "text-cyan-300" : "text-cobalt",
  };

  if (compact) {
    return (
      <div className={`rounded-2xl border p-4 ${t.card}`} data-testid="enterprise-crm-compact">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`flex items-center gap-1.5 text-xs font-bold ${t.eyebrow}`}><Plug2 className="w-4 h-4" /> Enterprise CRM Compatible</span>
          <span className={`text-[11px] ${t.sub}`}>{cat.messaging.headline}</span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {cat.live.map((p) => (
            <span key={p.key} className={`flex items-center gap-1.5 rounded-full pl-1 pr-3 py-1 text-xs font-semibold ${dark ? "bg-emerald-400/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`} data-testid={`crm-compact-live-${p.key}`}>
              <CrmTile k={p.key} name={p.name} size="sm" /> {p.name} <CheckCircle2 className="w-3.5 h-3.5" />
            </span>
          ))}
          {cat.priority.map((p) => (
            <span key={p.key} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${dark ? "bg-violet-400/10 text-violet-300" : "bg-violet-50 text-violet-700"}`} data-testid={`crm-compact-priority-${p.key}`}>
              <Lock className="w-3 h-3" /> {p.name} — {p.badge}
            </span>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {cat.coming_soon.map((p) => (
            <span key={p.key} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${t.chip}`} data-testid={`crm-compact-soon-${p.key}`}>
              <Clock3 className="w-3 h-3 inline mr-1 -mt-0.5" />{p.name} · Coming Soon
            </span>
          ))}
        </div>
        <p className={`mt-2 text-[11px] ${t.sub}`}>Live sync of cases, contacts, evidence &amp; timeline — OAuth 2.0, Keep-in-Sync, encrypted tokens, immutable audit.</p>
      </div>
    );
  }

  return (
    <div data-testid="enterprise-crm-section">
      <p className={`text-xs font-semibold uppercase tracking-widest ${t.eyebrow}`}>Enterprise CRM Compatible</p>
      <h2 className={`mt-2 font-heading text-2xl md:text-3xl font-bold tracking-tight max-w-2xl ${t.heading}`}>{cat.messaging.headline}</h2>
      <p className={`mt-3 max-w-2xl text-sm ${t.sub}`}>{cat.messaging.sub}</p>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="crm-sales-points">
        {cat.messaging.points.map((p) => (
          <span key={p} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${dark ? "bg-white/5 text-slate-300" : "bg-white border border-slate-100 text-slate-600"}`}>
            <Sparkles className={`w-3 h-3 ${t.eyebrow}`} /> {p}
          </span>
        ))}
      </div>

      <h3 className={`mt-8 font-heading text-base font-bold ${t.heading}`} data-testid="crm-live-heading">Live Integrations</h3>
      <div className="mt-3 grid sm:grid-cols-3 gap-4">
        {cat.live.map((p) => (
          <div key={p.key} className={`rounded-3xl border p-5 transition-all hover:-translate-y-0.5 ${t.card}`} data-testid={`crm-live-tier-${p.key}`}>
            <div className="flex items-center gap-3">
              <CrmTile k={p.key} name={p.name} dark={dark} />
              <div className={`font-heading font-bold ${t.heading}`}>{p.name}</div>
            </div>
            <div className="mt-3 space-y-1.5">
              {p.badges.map((b) => (
                <div key={b} className={`flex items-center gap-1.5 text-xs ${t.sub}`}>
                  <CheckCircle2 className={`w-3.5 h-3.5 ${dark ? "text-emerald-400" : "text-emerald-500"}`} /> {b}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <h3 className={`mt-8 font-heading text-base font-bold ${t.heading}`} data-testid="crm-priority-heading">Priority Integration</h3>
      <div className="mt-3 grid sm:grid-cols-2 gap-4">
        {cat.priority.map((p) => (
          <div key={p.key} className={`rounded-3xl border p-5 ${t.card}`} data-testid={`crm-priority-tier-${p.key}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <CrmTile k={p.key} name={p.name} dark={dark} />
                <div className={`font-heading font-bold ${t.heading}`}>{p.name}</div>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${dark ? "bg-violet-400/10 text-violet-300" : "bg-violet-50 text-violet-700"}`}><Lock className="w-3 h-3" /> {p.badge}</span>
            </div>
            <p className={`mt-3 text-xs leading-relaxed ${t.sub}`}>{p.explanation}</p>
          </div>
        ))}
      </div>

      <h3 className={`mt-8 font-heading text-base font-bold ${t.heading}`} data-testid="crm-soon-heading">Coming Soon</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {cat.coming_soon.map((p) => (
          <span key={p.key} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium ${t.chip}`} data-testid={`crm-soon-tier-${p.key}`}>
            <Clock3 className="w-3.5 h-3.5" /> {p.name}
          </span>
        ))}
      </div>

      {showCapabilities && <div className="mt-8"><EnterpriseCapabilityPanel dark={dark} /></div>}
      <div className="mt-6"><EnterpriseValuePanel dark={dark} /></div>
    </div>
  );
};

export const CrmCoverageBadges = ({ attorneyId, title = "Enterprise CRM Compatible", emptyHint = false, card = false }) => {
  const [cov, setCov] = useState(null);
  useEffect(() => {
    if (!attorneyId) return;
    api.get(`/attorneys/${attorneyId}/crm-coverage`).then(({ data }) => setCov(data.coverage)).catch(() => setCov([]));
  }, [attorneyId]);
  if (!cov) return null;
  if (!cov.length && !emptyHint) return null;
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-xs font-bold text-cobalt"><Plug2 className="w-4 h-4" /> {title}</div>
      {cov.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="crm-coverage-badges">
          {cov.map((c) => (
            <span key={c.provider} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold" data-testid={`coverage-${c.provider}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> {c.name}
              {c.keep_synced && <span className="ml-1 flex items-center gap-0.5 text-[10px] text-cobalt"><RefreshCw className="w-2.5 h-2.5" /> Keep-in-Sync</span>}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-500" data-testid="crm-coverage-empty">
          No live CRM connected yet — <Link to="/integrations" className="text-cobalt font-medium hover:underline">connect your firm's CRM</Link> to display coverage to clients and your firm.
        </p>
      )}
    </>
  );
  if (!card) return <div data-testid="crm-coverage">{body}</div>;
  return <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="crm-coverage">{body}</div>;
};

export default EnterpriseCrmSection;

const SYNC_BENEFITS = [
  "Client automatically created", "Matter/Case automatically created", "Timeline synchronized",
  "Evidence synchronized", "Nurse documentation synchronized", "Drone media synchronized",
  "Documents synchronized", "Future updates automatically pushed through Keep-in-Sync",
];

/* Qualitative value messaging only — no unverified numerical ROI claims. */
const ENTERPRISE_VALUE = [
  "Eliminate duplicate data entry", "Automatically create clients and matters",
  "Sync evidence automatically", "Sync accident timelines automatically",
  "Keep nurse documentation connected", "Sync drone media", "Secure document delivery",
  "Continuous Keep-in-Sync", "Enterprise-grade audit trail",
  "Works with the software your firm already uses",
];

export const EnterpriseValuePanel = ({ dark = false, compact = false }) => {
  const t = {
    card: dark ? "bg-white/5 border-white/10" : "bg-white border-slate-100",
    heading: dark ? "text-white" : "text-navy",
    item: dark ? "text-slate-300" : "text-slate-600",
    statement: dark ? "text-cyan-300" : "text-cobalt",
  };
  return (
    <div className={`rounded-2xl border ${compact ? "p-4" : "p-5"} ${t.card}`} data-testid="enterprise-value-panel">
      <h4 className={`font-heading font-bold ${compact ? "text-sm" : "text-base"} ${t.heading}`}>Why Connect Your CRM?</h4>
      <div className={`mt-3 grid ${compact ? "grid-cols-1 sm:grid-cols-2 gap-1.5" : "grid-cols-1 sm:grid-cols-2 gap-2"}`}>
        {ENTERPRISE_VALUE.map((v) => (
          <div key={v} className={`flex items-center gap-1.5 ${compact ? "text-[11px]" : "text-xs"} ${t.item}`}>
            <CheckCircle2 className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"} shrink-0 ${dark ? "text-emerald-400" : "text-emerald-500"}`} /> {v}
          </div>
        ))}
      </div>
      <p className={`mt-3 ${compact ? "text-[11px]" : "text-xs"} font-semibold italic ${t.statement}`} data-testid="enterprise-value-statement">
        Spend time practicing law—not moving data between systems.
      </p>
    </div>
  );
};

/* Shown to the PURCHASING ATTORNEY inside the case-claim workflow. */
export const ClaimCrmPanel = () => {
  const { user } = useAuth();
  const [cov, setCov] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!user?.user_id) return;
    api.get(`/attorneys/${user.user_id}/crm-coverage`).then(({ data }) => setCov(data.coverage)).catch(() => setCov([]));
  }, [user?.user_id]);
  if (!cov) return null;
  return (
    <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4" data-testid="claim-crm-panel">
      <div className="text-xs font-bold text-cobalt flex items-center gap-1.5"><Plug2 className="w-3.5 h-3.5" /> Your Connected CRM</div>
      {cov.length ? (
        <>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {cov.map((c) => (
              <span key={c.provider} data-testid={`claim-crm-${c.provider}`} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" /> {c.name} Connected
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-600 font-medium" data-testid="claim-crm-reassurance">This case will automatically synchronize into your connected CRM after purchase.</p>
          <button type="button" onClick={() => setOpen(!open)} data-testid="claim-crm-benefits-toggle" className="mt-1.5 text-[11px] font-semibold text-cobalt hover:underline">
            {open ? "Hide details" : "What syncs automatically?"}
          </button>
          {open && (
            <div className="mt-2 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="claim-crm-benefits">
                {SYNC_BENEFITS.map((b) => (
                  <div key={b} className="flex items-center gap-1.5 text-[11px] text-slate-500"><CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" /> {b}</div>
                ))}
              </div>
              <EnterpriseValuePanel compact />
            </div>
          )}
        </>
      ) : (
        <>
          <p className="mt-2 text-xs text-slate-500" data-testid="claim-crm-none">
            No CRM connected yet — <Link to="/integrations" className="text-cobalt font-medium hover:underline">connect Clio, PracticePanther or MyCase</Link> and every case you claim syncs automatically. No duplicate data entry.
          </p>
          <div className="mt-2"><EnterpriseValuePanel compact /></div>
        </>
      )}
    </div>
  );
};

/* CLIENT-facing: never reveals the CRM — generic secure-connection reassurance. */
export const SecureDeliveryNote = ({ attorneyId }) => {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    if (!attorneyId) return;
    api.get(`/attorneys/${attorneyId}/crm-coverage`).then(({ data }) => setConnected((data.coverage || []).length > 0)).catch(() => {});
  }, [attorneyId]);
  if (!connected) return null;
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center gap-3" data-testid="secure-delivery-note">
      <span className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-emerald-600" /></span>
      <div>
        <div className="text-sm font-semibold text-navy">Your legal team is securely connected</div>
        <div className="text-xs text-slate-500">Your documents and case updates are securely delivered to your legal team.</div>
      </div>
    </div>
  );
};

const PROVIDER_LABELS = { clio: "Clio Manage", practicepanther: "PracticePanther", mycase: "MyCase", needles: "Neos (Needles)" };

/* Attorney-facing: CRM public-API document limitation — informational, never a Crash Ready error. */
export const DocSyncLimitationBanner = ({ providers = [], className = "" }) => (
  <div className={`rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 ${className}`} data-testid="doc-sync-limitation-banner">
    <span className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><Info className="w-4.5 h-4.5 text-amber-600" /></span>
    <div>
      <div className="text-sm font-bold text-amber-800">Document Sync Limitation{providers.length ? ` — ${providers.join(", ")}` : ""}</div>
      <p className="mt-1 text-xs text-amber-700 leading-relaxed">
        Your connected CRM does not currently support secure document uploads through its public API. Crash Ready will continue syncing all supported data (case information, notes, timeline events, contacts, and metadata), but medical records and provider documents must be delivered outside of the CRM.
      </p>
      <p className="mt-1 text-[10px] text-amber-600">This is a limitation of the CRM's public API — not a Crash Ready error.</p>
    </div>
  </div>
);

/* Case-scoped banner. audience="attorney" → Document Sync Limitation; audience="provider" → Attorney CRM Limitation. */
export const CaseDocSyncBanner = ({ caseId, audience = "attorney", className = "" }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!caseId) return;
    api.get(`/cases/${caseId}/crm-doc-support`).then(({ data }) => setD(data)).catch(() => {});
  }, [caseId]);
  if (!d?.limited) return null;
  if (audience === "provider") {
    return (
      <div className={`rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 ${className}`} data-testid="provider-crm-limitation-notice">
        <span className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><Info className="w-4.5 h-4.5 text-amber-600" /></span>
        <div>
          <div className="text-sm font-bold text-amber-800">Attorney CRM Limitation</div>
          <p className="mt-1 text-xs text-amber-700 leading-relaxed">
            The receiving law firm's CRM does not currently accept document uploads through its public API. Please securely deliver all finalized medical records and supporting documentation directly to the law firm using the firm's approved secure delivery method.
          </p>
          <p className="mt-1 text-[10px] text-amber-600">Crash Ready still notifies the attorney, tracks completion, and records delivery in the audit log.</p>
        </div>
      </div>
    );
  }
  return <DocSyncLimitationBanner providers={d.limited_providers || []} className={className} />;
};


/* Firm operational card: connection health, per-type sync counts, Sync Now / Pause / Reconnect. */
export const EnterpriseConnectionStatus = ({ dark = false }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/crm/oauth/dashboard").then(({ data }) => setD(data)).catch(() => setD({ connections: [] }));
  useEffect(() => { load(); }, []);
  if (!d) return null;

  const t = {
    card: dark ? "bg-[#0E1624] border-[#1E2C45]" : "bg-white border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]",
    heading: dark ? "text-white" : "text-navy",
    sub: dark ? "text-slate-400" : "text-slate-500",
    stat: dark ? "bg-[#152033]" : "bg-slate-50",
    btn: dark ? "bg-[#152033] border border-[#1E2C45] text-slate-300 hover:text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50",
  };
  const syncNow = async (p) => {
    setBusy(`sync-${p}`);
    try { const { data } = await api.post(`/crm/oauth/${p}/sync-all`); toast.success(`Queued ${data.queued} item(s) · ${data.succeeded} synced`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); } finally { setBusy(""); }
  };
  const pause = async (p, enabled) => {
    try { await api.post(`/crm/oauth/${p}/keep-sync`, { enabled }); toast.success(`Keep-in-Sync ${enabled ? "resumed" : "paused"}`); load(); }
    catch { toast.error("Could not update"); }
  };
  const reconnect = async (p) => {
    setBusy(`re-${p}`);
    try { const { data } = await api.get(`/crm/oauth/${p}/authorize`); window.location.href = data.auth_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Reconnect unavailable"); setBusy(""); }
  };
  const HEALTH = { healthy: ["bg-emerald-500", "Healthy"], error: ["bg-red-500", "Needs attention"], reauth_required: ["bg-amber-500", "Reauth required"] };

  return (
    <div className={`rounded-3xl border p-5 ${t.card}`} data-testid="enterprise-connection-status">
      <div className="flex items-center gap-2">
        <Activity className={`w-5 h-5 ${dark ? "text-blue-400" : "text-cobalt"}`} />
        <h3 className={`font-heading font-bold ${t.heading}`}>Enterprise Connection Status</h3>
      </div>
      {!d.connections.length ? (
        <p className={`mt-3 text-sm ${t.sub}`} data-testid="connection-status-empty">
          No live CRM connected. <Link to="/integrations" className={`font-medium hover:underline ${dark ? "text-blue-400" : "text-cobalt"}`}>Connect your firm's CRM</Link> — claimed cases, evidence and timelines then sync automatically.
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          {d.connections.map((c) => {
            const [dot, label] = HEALTH[c.health] || ["bg-slate-400", c.health];
            const bt = c.synced_by_type || {};
            const docs = (bt.evidence || 0) + (bt.document || 0);
            return (
              <div key={c.provider} data-testid={`connection-status-${c.provider}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
                  <span className={`font-heading font-bold text-sm ${t.heading}`}>{PROVIDER_LABELS[c.provider] || c.provider}</span>
                  <span className={`text-xs ${t.sub}`}>{label}</span>
                  <span className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full ${c.keep_synced ? (dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-600") : (dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-50 text-amber-600")}`} data-testid={`keepsync-status-${c.provider}`}>
                    Keep-in-Sync {c.keep_synced ? "active" : "paused"}
                  </span>
                  {c.capabilities && c.capabilities.documents === false && (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-50 text-amber-700"}`} data-testid={`doc-unsupported-${c.provider}`}>
                      <AlertTriangle className="w-3 h-3" /> Document Uploads Not Supported by CRM
                    </span>
                  )}
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  {[["Last Sync", c.last_sync_at ? new Date(c.last_sync_at).toLocaleDateString() : "—"],
                    ["Documents Synced", docs], ["Timeline Synced", bt.timeline || 0], ["Failed", c.failed]].map(([l, v]) => (
                    <div key={l} className={`rounded-xl py-2 ${t.stat}`} data-testid={`stat-${c.provider}-${String(l).toLowerCase().replace(/\s/g, "-")}`}>
                      <div className={`font-heading font-extrabold text-sm ${t.heading}`}>{v}</div>
                      <div className={`text-[9px] uppercase ${t.sub}`}>{l}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => syncNow(c.provider)} disabled={busy === `sync-${c.provider}`} data-testid={`sync-now-${c.provider}`}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold ${dark ? "bg-blue-600 text-white" : "bg-cobalt text-white hover:bg-cobalt-hover"}`}>
                    {busy === `sync-${c.provider}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync Now
                  </button>
                  <button onClick={() => pause(c.provider, !c.keep_synced)} data-testid={`pause-sync-${c.provider}`}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold ${t.btn}`}>
                    {c.keep_synced ? <PauseCircle className="w-3.5 h-3.5" /> : <PlayCircle className="w-3.5 h-3.5" />} {c.keep_synced ? "Pause Sync" : "Resume Sync"}
                  </button>
                  <button onClick={() => reconnect(c.provider)} disabled={busy === `re-${c.provider}`} data-testid={`reconnect-${c.provider}`}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold ${t.btn}`}>
                    {busy === `re-${c.provider}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug2 className="w-3.5 h-3.5" />} Reconnect
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
